/**
 * Typeahead for the activity log's Quelle filter.
 *
 * Purpose-built rather than an extension of alpineSelect. That component takes a flat,
 * preloaded array with a client-side filterFn and renders a flat list; this one needs a
 * server round trip, group headers, and a hidden input so the choice survives a plain GET
 * form submit. Retrofitting all three onto alpineSelect would have rewritten most of it
 * and put its two existing callers (auth/choose, superadmin/ameise/importe) at risk for no
 * gain to either.
 *
 * The empty value means "Alle Quellen" and must stay submittable: an unset filter and a
 * filter set to nothing are the same query, and the user needs a way back to it.
 */
const DEBOUNCE_MS = 250

document.addEventListener('alpine:init', () => {
    window.Alpine.data('filterCauserSelect', (endpoint, initialValue = '', initialLabel = '') => ({
        endpoint,
        value: initialValue,
        label: initialLabel,
        search: '',
        open: false,
        groups: [],
        loading: false,
        loaded: false,
        focusedIndex: null,
        timer: null,
        /** Ticket counter; see load(). Only the newest request may write. */
        requestId: 0,

        /** Flattened in render order, which is what the arrow keys walk. */
        get options() {
            return this.groups.flatMap((group) => group.options)
        },

        get displayText() {
            return this.value === '' ? 'Alle Quellen' : this.label
        },

        openPanel() {
            const hadSearch = this.search !== ''
            this.open = true
            this.search = ''
            // Fetched on first open rather than on page load: most visits to the activity
            // log never touch this filter, and the request is not free.
            //
            // Refetched when reopening after a search, because the panel would otherwise
            // show that search's narrowed results under a now-empty search box — reading
            // as the full list, with everything that did not match the old term appearing
            // not to exist.
            if (!this.loaded || hadSearch) {
                this.load()
            }
        },

        closePanel() {
            this.open = false
            this.focusedIndex = null
        },

        toggle() {
            this.open ? this.closePanel() : this.openPanel()
        },

        onInput() {
            // Debounced because this fires per keystroke; without it a ten-character name
            // is ten requests, and the rate limiter would be the only thing stopping them.
            clearTimeout(this.timer)
            this.timer = setTimeout(() => this.load(), DEBOUNCE_MS)
        },

        async load() {
            // Every call takes a ticket, and only the newest holder may write. Two
            // requests are in flight routinely: openPanel() fires one immediately and a
            // keystroke fires another 250ms later. Nothing orders their *responses*, and
            // the first is the unfiltered query — the more expensive of the pair, so it
            // is the likelier to land last. "Last response wins" therefore replaces the
            // narrowed list with the full one while the search box still shows the term
            // the user typed: every source that does not match it appears to match,
            // which is the same wrong answer the refetch-on-reopen above exists to
            // prevent, reached from the other side.
            //
            // A ticket rather than an AbortController: the request is already debounced,
            // so this is about which answer is believed, not about saving the round trip.
            const request = ++this.requestId
            this.loading = true

            try {
                const url = `${this.endpoint}?q=${encodeURIComponent(this.search)}`
                const response = await fetch(url, {headers: {Accept: 'application/json'}})

                if (request !== this.requestId) {
                    return
                }

                // Leaves whatever was already listed in place. Blanking the panel on a
                // failed request would read as "no such source exists", which is a
                // different and wrong answer.
                if (!response.ok) {
                    return
                }

                const payload = await response.json()

                // Checked again: parsing the body is a second suspension point, and a
                // newer request can win it between the two.
                if (request !== this.requestId) {
                    return
                }

                this.groups = payload.groups ?? []
                this.loaded = true
                this.focusedIndex = null
            } catch {
                // Same reasoning as above.
            } finally {
                // A superseded request must not lower the flag out from under the live
                // one, or the panel stops saying it is loading while it still is.
                if (request === this.requestId) {
                    this.loading = false
                }
            }
        },

        select(option) {
            this.value = option.value
            this.label = option.label
            this.closePanel()
        },

        clear() {
            this.value = ''
            this.label = ''
            this.closePanel()
        },

        focusNext() {
            if (!this.open) {
                this.openPanel()

                return
            }
            const last = this.options.length - 1
            if (last < 0) {
                return
            }
            this.focusedIndex = this.focusedIndex === null || this.focusedIndex >= last
                ? 0
                : this.focusedIndex + 1
        },

        focusPrev() {
            const last = this.options.length - 1
            if (!this.open || last < 0) {
                return
            }
            this.focusedIndex = this.focusedIndex === null || this.focusedIndex <= 0
                ? last
                : this.focusedIndex - 1
        },

        selectFocused() {
            if (!this.open) {
                return
            }
            const option = this.options[this.focusedIndex ?? 0]
            if (option !== undefined) {
                this.select(option)
            }
        },

        /** Index within the flattened list, so group headers do not break arrow-key order. */
        indexOf(groupIndex, optionIndex) {
            return this.groups
                .slice(0, groupIndex)
                .reduce((total, group) => total + group.options.length, 0) + optionIndex
        },
    }))
})
